import { Injectable } from '@angular/core';
import {
  CanActivate,
  Router,
  ActivatedRouteSnapshot,
  RouterStateSnapshot,
  UrlTree,
} from '@angular/router';
import { environment } from 'src/environments/environment';

const toLowerSet = (
  emails: readonly string[] | undefined,
): ReadonlySet<string> => new Set((emails || []).map((e) => e.toLowerCase()));

export const RESTRICTED_MENU_EMAILS: ReadonlySet<string> = toLowerSet(
  (environment as any).restrictedMenuEmails,
);

export const HIDDEN_VENDOR_REPORTS_EMAILS: ReadonlySet<string> = toLowerSet(
  (environment as any).hiddenVendorReportsEmails,
);

/**
 * Restricted-menu accounts that are ALSO allowed into the Communication Hub.
 *
 * Deliberately its own list rather than reusing RESTRICTED_MENU_EMAILS. Those
 * two sets happen to hold the same single address today, but they answer
 * different questions — "whose menu is cut down" vs "who may read and reply to
 * vendor conversations". Keyed off the first, the next restricted account added
 * for an unrelated reason would silently inherit every vendor conversation.
 *
 * Mirrors the server's COMMS_OVERSIGHT_EMAILS, which is what actually enforces
 * this. A name here only decides what is OFFERED; the server decides what is
 * allowed, and will 403 an address it does not recognise.
 */
export const COMMS_HUB_EMAILS: ReadonlySet<string> = toLowerSet(
  (environment as any).commsHubEmails,
);

function getCurrentEmail(): string {
  try {
    const raw = localStorage.getItem('userInfo');
    if (!raw) return '';
    const parsed = JSON.parse(raw);
    return String(parsed?.email || '').toLowerCase();
  } catch {
    return '';
  }
}

export function isRestrictedEmailUser(): boolean {
  return RESTRICTED_MENU_EMAILS.has(getCurrentEmail());
}

export function isVendorReportsHiddenUser(): boolean {
  return HIDDEN_VENDOR_REPORTS_EMAILS.has(getCurrentEmail());
}

/**
 * May this account open the Communication Hub?
 *
 * Ordinary internal staff: yes — it is part of their console. A restricted-menu
 * account: only if it is named in COMMS_HUB_EMAILS. Anyone else with a
 * restricted menu gets nothing, which is the point of the separate list.
 */
export function canUseCommsHub(): boolean {
  if (!isRestrictedEmailUser()) return true;
  return COMMS_HUB_EMAILS.has(getCurrentEmail());
}

/**
 * May this account open Insights?
 *
 * Same shape as canUseCommsHub: ordinary internal staff yes, a restricted-menu
 * account only when it is one we have named. Reuses COMMS_HUB_EMAILS rather
 * than inventing a second list — prodcontroller is the account both exist for,
 * and two lists that must agree will eventually not.
 */
export function canUseInsights(): boolean {
  if (!isRestrictedEmailUser()) return true;
  return COMMS_HUB_EMAILS.has(getCurrentEmail());
}

@Injectable({ providedIn: 'root' })
export class RestrictedEmailGuard implements CanActivate {
  constructor(private router: Router) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): boolean | UrlTree {
    if (!isRestrictedEmailUser()) return true;
    return this.router.parseUrl('/workspace/ops-console/vendor-reports');
  }
}

/**
 * Inverse of RestrictedEmailGuard — allow ONLY restricted-menu users
 * (prodcontroller@studioip.ai) through. Everyone else is bounced back to
 * the ops-console dashboard. Used by routes that are restricted-user-only,
 * like the global project heatmap.
 */
@Injectable({ providedIn: 'root' })
export class ProdControllerOnlyGuard implements CanActivate {
  constructor(private router: Router) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): boolean | UrlTree {
    if (isRestrictedEmailUser()) return true;
    return this.router.parseUrl('/workspace/ops-console/dashboard');
  }
}

/**
 * Guards the Communication Hub route.
 *
 * Internal staff pass. A restricted-menu account passes only when named in
 * COMMS_HUB_EMAILS — everyone else goes back to the screen they live on, so a
 * typed URL cannot reach vendor conversations.
 */
@Injectable({ providedIn: 'root' })
export class CommsHubGuard implements CanActivate {
  constructor(private router: Router) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): boolean | UrlTree {
    if (canUseCommsHub()) return true;
    return this.router.parseUrl('/workspace/ops-console/vendor-reports');
  }
}

/** Guards the Insights route. See CommsHubGuard — identical reasoning. */
@Injectable({ providedIn: 'root' })
export class InsightsGuard implements CanActivate {
  constructor(private router: Router) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): boolean | UrlTree {
    if (canUseInsights()) return true;
    return this.router.parseUrl('/workspace/ops-console/vendor-reports');
  }
}

@Injectable({ providedIn: 'root' })
export class VendorReportsHiddenGuard implements CanActivate {
  constructor(private router: Router) {}

  canActivate(
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
  ): boolean | UrlTree {
    if (!isVendorReportsHiddenUser()) return true;
    return this.router.parseUrl('/workspace/ops-console/dashboard');
  }
}
